import { useEffect, useState } from 'react';
import { API_TIMEOUT_MS } from '@/services/ApiService';

/** The failure a read that never answered becomes (I-1702, KD-17). */
export class AccessReadTimeoutError extends Error {
  constructor(readonly timeoutMs: number) {
    super(`No answer within ${Math.round(timeoutMs / 1000)} seconds`);
    this.name = 'AccessReadTimeoutError';
  }
}

/**
 * A read a gated control waits on, bounded in time (I-1702, KD-17): it rejects after the app's
 * standard request timeout (`API_TIMEOUT_MS`) if it has not settled by then. An HTTP read is already
 * bounded by axios, but a read that also waits on a local cache, a data source's discovery or a
 * socket is not, and without this a control waiting on it stayed "checking access" for good.
 */
export function settleWithin<T>(read: Promise<T>, timeoutMs: number = API_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new AccessReadTimeoutError(timeoutMs)), timeoutMs);
    read.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (cause: unknown) => {
        clearTimeout(timer);
        reject(cause);
      },
    );
  });
}

/**
 * The same bound for a wait that is not a promise of the screen's own — a terminal watch starting
 * inside its hook (I-1707, KD-17b): true once `waiting` has held for `timeoutMs` without a break,
 * false again as soon as it stops. A gate reads it as its read's `settled`.
 */
export function useWaitBound(waiting: boolean, timeoutMs: number = API_TIMEOUT_MS): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    setExpired(false);
    if (!waiting) return undefined;
    const timer = setTimeout(() => setExpired(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [waiting, timeoutMs]);
  return waiting && expired;
}
